// ── V143: 다른 업체에 문의 (询价清单) — 단가표 → 위챗으로 보낼 품목 목록 ─────────────
// 발주처(快驴进货·타오바오 등)를 고르면 그 품목들을 중국어 목록(이름·규격·브랜드·구매 링크)으로 만든다.
// 가격은 넣지 않는다. 인사말은 고칠 수 있고, 필요 없는 품목은 빼고 보낸다.
import React, { useMemo, useState } from 'react';
import { useLang } from '../i18n/LangContext';
import { useIsMobile } from '../useIsMobile';
import { inquiryRows, buildInquiryText, defaultGreeting, itemInfo, CATEGORY_ZH } from '../utils/inquiryList';

const LS_VENDORS = 'semfit_inquiry_vendors';
const LS_GREETING = 'semfit_inquiry_greeting';
const readLS = (k, d) => { try { const v = localStorage.getItem(k); return v == null ? d : JSON.parse(v); } catch (e) { return d; } };
const writeLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* 무시 */ } };
const rowKey = (p) => [p.code, p.name, p.vendor].map(x => String(x == null ? '' : x).trim()).join('::');

export default function InquiryListModal({ prices = [], onClose }) {
  const { lang } = useLang();
  const isMobile = useIsMobile();
  const bizName = (() => { try { return localStorage.getItem('ddabak_biz_name') || ''; } catch (e) { return ''; } })();

  // 발주처 목록 (반제품 제외, 품목 수)
  const vendors = useMemo(() => {
    const m = new Map();
    for (const p of prices || []) {
      if (!p || !String(p.name || '').trim() || p.category === '반제품') continue;
      const v = String(p.vendor || '').trim();
      if (!v) continue;
      const e = m.get(v) || { name: v, zh: '', count: 0, links: 0 };
      e.count++;
      if (p.link) e.links++;
      if (!e.zh && p.vendor_zh && !/[가-힣]/.test(p.vendor_zh)) e.zh = String(p.vendor_zh).trim();
      m.set(v, e);
    }
    return [...m.values()].sort((a, b) => b.count - a.count);
  }, [prices]);

  const [picked, setPicked] = useState(() => readLS(LS_VENDORS, []).filter(v => vendors.some(x => x.name === v)));
  const [greeting, setGreeting] = useState(() => readLS(LS_GREETING, '') || defaultGreeting(bizName));
  const [withBrand, setWithBrand] = useState(true);
  const [withKo, setWithKo] = useState(false);
  const [off, setOff] = useState({});        // 뺀 품목 (줄 키)
  const [copied, setCopied] = useState(-1);

  const togglePick = (v) => {
    const next = picked.includes(v) ? picked.filter(x => x !== v) : [...picked, v];
    setPicked(next); writeLS(LS_VENDORS, next); setCopied(-1);
  };
  const rows = useMemo(() => inquiryRows(prices, picked), [prices, picked]);
  const isOn = (p) => (off[rowKey(p)] === undefined ? !p.hiddenInOrder : !off[rowKey(p)]);
  const chosen = rows.filter(isOn);
  const { parts, count } = useMemo(() => buildInquiryText(chosen, { greeting, withBrand, withKo }),
    [chosen, greeting, withBrand, withKo]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (i) => {
    const text = parts[i];
    if (isMobile && typeof navigator !== 'undefined' && navigator.share) {
      try { await navigator.share({ text }); setCopied(i); return; }
      catch (e) { if (e && e.name === 'AbortError') return; }
    }
    try { await navigator.clipboard.writeText(text); setCopied(i); }
    catch (e) {
      const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); setCopied(i); } catch (err) { alert(text); }
      ta.remove();
    }
  };

  const chip = (on) => ({
    padding: '6px 12px', borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: 'pointer',
    border: on ? '1.5px solid #1A56A0' : '1.5px solid #E4E4E7', background: on ? '#EFF6FF' : '#fff', color: on ? '#1A56A0' : '#52525B',
  });
  const L = (ko, zh) => (lang === 'zh' ? zh : ko);

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose && onClose(); }} data-inquiry-modal
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 4000, display: 'flex',
               alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: isMobile ? '20px 20px 0 0' : 16, width: isMobile ? '100%' : 720, maxWidth: '96vw',
                    maxHeight: isMobile ? '94vh' : '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#18181B' }}>💬 {L('다른 업체에 문의 · 询价清单', '询价清单 · 다른 업체에 문의')}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              {L('고른 발주처의 품목을 중국어 목록으로 만들어요. 가격은 넣지 않아요.', '把所选供应商的食材整理成清单发微信。不含价格。')}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af' }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
          {/* ① 발주처 */}
          <div style={{ fontSize: 12, fontWeight: 800, color: '#374151', marginBottom: 6 }}>① {L('어느 발주처 품목을 물어볼까요?', '选择供应商')}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {vendors.map(v => (
              <button key={v.name} type="button" data-inq-vendor={v.name} onClick={() => togglePick(v.name)} style={chip(picked.includes(v.name))}>
                {picked.includes(v.name) ? '✓ ' : ''}{v.name}{v.zh && v.zh !== v.name ? ` · ${v.zh}` : ''}
                <span style={{ fontWeight: 500, opacity: 0.7, marginLeft: 4 }}>{v.count}{v.links ? ` · 🔗${v.links}` : ''}</span>
              </button>
            ))}
            {!vendors.length && <span style={{ fontSize: 12, color: '#9ca3af' }}>{L('단가표에 발주처가 입력된 품목이 없어요', '单价表里没有填写供应商的食材')}</span>}
          </div>

          {/* ② 인사말 */}
          <div style={{ fontSize: 12, fontWeight: 800, color: '#374151', margin: '14px 0 6px' }}>② {L('인사말 (고칠 수 있어요)', '开头的话（可以修改）')}</div>
          <textarea value={greeting} onChange={e => { setGreeting(e.target.value); writeLS(LS_GREETING, e.target.value); setCopied(-1); }} rows={3}
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: 13, lineHeight: 1.5, fontFamily: 'inherit' }} />
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 6, fontSize: 12, color: '#374151' }}>
            <label style={{ cursor: 'pointer' }}><input type="checkbox" checked={withBrand} onChange={e => setWithBrand(e.target.checked)} /> {L('브랜드(제조사) 넣기', '写品牌')}</label>
            <label style={{ cursor: 'pointer' }}><input type="checkbox" checked={withKo} onChange={e => setWithKo(e.target.checked)} /> {L('한국어 이름도 같이', '也写韩文名')}</label>
            <button type="button" onClick={() => { const g = defaultGreeting(bizName); setGreeting(g); writeLS(LS_GREETING, g); }}
              style={{ border: 'none', background: 'none', color: '#1A56A0', fontSize: 12, cursor: 'pointer', padding: 0 }}>
              {L('인사말 처음대로', '恢复默认')}
            </button>
          </div>

          {/* ③ 품목 */}
          {rows.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#374151', margin: '14px 0 6px' }}>
                ③ {L(`품목 ${chosen.length}/${rows.length} (빼려면 체크 해제)`, `食材 ${chosen.length}/${rows.length}（不需要的取消勾选）`)}
              </div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, maxHeight: 220, overflowY: 'auto', padding: '4px 0' }}>
                {rows.map(p => {
                  const info = itemInfo(p, { withBrand, withKo: true });
                  const k = rowKey(p);
                  return (
                    <label key={k} data-inq-item={p.name} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '5px 10px', fontSize: 12, cursor: 'pointer',
                                                  color: isOn(p) ? '#18181B' : '#A1A1AA' }}>
                      <input type="checkbox" checked={isOn(p)} onChange={e => { setOff(m => ({ ...m, [k]: !e.target.checked })); setCopied(-1); }} style={{ marginTop: 2 }} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <b>{info.name}</b>
                        {info.spec && <span style={{ color: '#6b7280' }}> · {info.spec}</span>}
                        {info.brand && <span style={{ color: '#6b7280' }}> · {info.brand}</span>}
                        <span style={{ color: '#9ca3af' }}> · {p.vendor}{CATEGORY_ZH[p.category] ? ` · ${CATEGORY_ZH[p.category]}` : ''}</span>
                        {info.link && <span style={{ color: '#1A56A0' }}> · 🔗</span>}
                        {p.hiddenInOrder && <span style={{ color: '#9ca3af' }}> · {L('발주서에서 감춤', '订货单里已隐藏')}</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
            </>
          )}

          {/* ④ 보낼 글 */}
          {count > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#374151', margin: '14px 0 6px' }}>
                ④ {L('위챗으로 보낼 글', '发微信的内容')}
                {parts.length > 1 && <span style={{ fontWeight: 600, color: '#B45309' }}> · {L(`길어서 ${parts.length}번에 나눠 보내요`, `内容较长，分 ${parts.length} 次发送`)}</span>}
              </div>
              {parts.map((text, i) => (
                <div key={i} style={{ marginBottom: 10, border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                  <pre data-inq-text={i} style={{ margin: 0, padding: '10px 12px', whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: 12,
                                                    lineHeight: 1.6, background: '#FAFAFA', maxHeight: 260, overflowY: 'auto', fontFamily: 'inherit',
                                                    userSelect: 'text', WebkitUserSelect: 'text' }}>{text}</pre>
                  <button type="button" data-inq-send={i} onClick={() => send(i)}
                    style={{ width: '100%', padding: '11px', border: 'none', background: copied === i ? '#16A34A' : '#07C160', color: '#fff',
                             fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
                    {copied === i ? L('✓ 복사됐어요 — 위챗에 붙여넣으세요 · 已复制', '✓ 已复制，粘贴到微信 · 복사됨')
                      : `💬 ${L('위챗으로 보내기', '发微信')}${parts.length > 1 ? ` (${i + 1}/${parts.length})` : ''} · ${L('发微信', '복사')}`}
                  </button>
                </div>
              ))}
              <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.6 }}>
                {L('타오바오 링크는 한 줄에 따로 넣어서 위챗에서 누르면 바로 열려요. 가격은 들어가지 않아요.',
                   '淘宝链接单独一行，在微信里可以直接点开。不含价格。')}
              </div>
            </>
          )}
          {picked.length > 0 && count === 0 && (
            <div style={{ marginTop: 14, fontSize: 13, color: '#9ca3af' }}>{L('보낼 품목이 없어요', '没有要发送的食材')}</div>
          )}
        </div>
      </div>
    </div>
  );
}
